import { BaseLogo } from "../../../core/models/entities/BaseLogo"
import { Design } from "../../../core/models/entities/Design"
import { Role } from "../../../core/models/entities/Role"
import { useApprovalDialogAdmin } from "./approval-dialog-admin"
import { useApprovalDialogCustomer } from "./approval-dialog-customer"
import { useApprovalDialogVendor } from "./approval-dialog-vendor"

export interface Props<T> {
  role: Role
  subject: string
  onSkip: (entity: T) => Promise<void>
  onOrder: (entity: T) => Promise<void>
  onInitApproval: (entity: T, email?: string) => Promise<void>
}

export interface ApprovalDialog<T> {
  component: JSX.Element,
  start: (entity: T) => void
}

export function useApprovalDialogByRole<T extends Design | BaseLogo>({ role, subject, onSkip, onOrder, onInitApproval }: Props<T>): ApprovalDialog<T> {
  const title = subject

  // Rules of hooks: this used to switch(role) between the three hooks, so the
  // mounted hook list changed LENGTH when a role flipped in-place — exactly what
  // happens on act-as-vendor EXIT (the warm profile cache flips Vendor→Admin with
  // no remount; only ENTER remounts via the ProfileContext gate). The admin branch
  // holds one more useState than the vendor branch → "Rendered more hooks than
  // during the previous render" → RouteErrorView on every reset. All three hooks
  // are therefore called unconditionally (they are cheap: dialog/useState reads
  // only — the inner dialog components with effects only run when rendered) and
  // the role picks which result is returned.
  const admin = useApprovalDialogAdmin<T>({
    subject: title,
    onSkip,
    onOrder,
    onInitApproval: (entity, email) => onInitApproval(entity, email)
  })
  const vendor = useApprovalDialogVendor<T>({
    subject: title,
    onSkip,
    onOrder,
    onInitApproval: (entity, email) => onInitApproval(entity, email)
  })
  const customer = useApprovalDialogCustomer<T>({
    subject: title,
    onSkip,
    onOrder,
    onInitApproval: (entity, email) => onInitApproval(entity, email)
  })

  switch (role) {
    case Role.Admin:
      return admin
    case Role.Vendor:
      return vendor
    case Role.EndUser:
    default: // Handle any other customer-type roles
      return customer
  }
}
